import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Shield className="h-7 w-7 text-teal-600" />
            <span className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Manrope' }}>
              AICreditPilot
            </span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
          Privacy Policy
        </h1>
        <p className="text-slate-500 mb-8">Last updated: February 2026</p>

        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">1. Introduction</h2>
            <p className="text-slate-600 mb-4">
              AICreditPilot ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our credit repair and dispute automation services.
            </p>
            <p className="text-slate-600">
              By using our services, you agree to the collection and use of information in accordance with this policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">2. Information We Collect</h2>
            <h3 className="text-lg font-medium text-slate-800 mb-2">Personal Information</h3>
            <p className="text-slate-600 mb-4">We may collect the following types of personal information:</p>
            <ul className="list-disc list-inside text-slate-600 mb-4 space-y-2">
              <li>Name, email address, and phone number</li>
              <li>Mailing address</li>
              <li>Date of birth</li>
              <li>Last four digits of Social Security Number (for identity verification)</li>
              <li>Credit report information (when you authorize us to access it)</li>
              <li>Payment information (processed securely through Stripe)</li>
            </ul>
            <h3 className="text-lg font-medium text-slate-800 mb-2">Automatically Collected Information</h3>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>IP address and browser type</li>
              <li>Device information</li>
              <li>Usage data and analytics</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-slate-600 mb-4">We use the information we collect to:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>Provide, maintain, and improve our services</li>
              <li>Process credit dispute letters on your behalf</li>
              <li>Communicate with credit bureaus regarding your disputes</li>
              <li>Send you updates about your account and disputes</li>
              <li>Process payments and manage subscriptions</li>
              <li>Provide customer support</li>
              <li>Comply with legal obligations</li>
              <li>Detect and prevent fraud</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">4. Information Sharing and Disclosure</h2>
            <p className="text-slate-600 mb-4">We may share your information with:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li><strong>Credit Bureaus:</strong> To submit dispute letters on your behalf (Experian, Equifax, TransUnion)</li>
              <li><strong>Service Providers:</strong> Third-party vendors who assist in operating our services (payment processors, email providers, mailing services)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            </ul>
            <p className="text-slate-600 mt-4">
              We do not sell your personal information to third parties.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">5. Data Security</h2>
            <p className="text-slate-600 mb-4">
              We implement appropriate technical and organizational security measures to protect your personal information, including:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Secure authentication protocols</li>
              <li>Regular security assessments</li>
              <li>Access controls and monitoring</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">6. Your Rights</h2>
            <p className="text-slate-600 mb-4">You have the right to:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>Access and receive a copy of your personal data</li>
              <li>Correct inaccurate or incomplete information</li>
              <li>Request deletion of your personal data</li>
              <li>Opt-out of marketing communications</li>
              <li>Withdraw consent at any time</li>
            </ul>
            <p className="text-slate-600 mt-4">
              To exercise these rights, contact us at support@aicreditpilot.com.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">7. Cookies</h2>
            <p className="text-slate-600">
              We use cookies and similar tracking technologies to enhance your experience. You can control cookie preferences through your browser settings. Essential cookies required for the service to function cannot be disabled.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">8. Children's Privacy</h2>
            <p className="text-slate-600">
              Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">9. Changes to This Policy</h2>
            <p className="text-slate-600">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">10. Contact Us</h2>
            <p className="text-slate-600 mb-4">
              If you have questions about this Privacy Policy, please contact us:
            </p>
            <ul className="text-slate-600 space-y-2">
              <li><strong>Email:</strong> support@aicreditpilot.com</li>
              <li><strong>Phone:</strong> 1-800-697-4877</li>
            </ul>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center text-slate-500 text-sm">
          © 2026 AICreditPilot. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
